import React, { Component, ErrorInfo, ReactNode } from 'react';
import { toast } from 'sonner';
import { i18n } from '@/i18n';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const isChunkLoadError = (error: Error | null) => {
  const errorMsg = error?.message || '';
  const errorName = error?.name || '';

  return (
    errorName === 'ChunkLoadError' ||
    /Importing a module script failed/i.test(errorMsg) ||
    /Failed to fetch dynamically imported module/i.test(errorMsg) ||
    /error loading dynamically imported module/i.test(errorMsg) ||
    /Loading chunk [\w-]+ failed/i.test(errorMsg) ||
    /'text\/html'.*module script/i.test(errorMsg)
  );
};

const nukeCachesAndReload = () => {
  const hardReload = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('cb', String(Date.now()));
    window.location.replace(url.toString());
  };

  const unregisterSWs = async () => {
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => undefined)));
      }
    } catch {
      /* ignore */
    }
  };

  const clearCaches = async () => {
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
      }
    } catch {
      /* ignore */
    }
  };

  Promise.all([unregisterSWs(), clearCaches()]).finally(hardReload);
};

const ChunkErrorScreen = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: 24,
      background: '#0a0a0a',
      color: '#e0e0e0',
      fontFamily: 'system-ui',
      textAlign: 'center',
    }}
  >
    <p style={{ marginBottom: 8, fontSize: 16, fontWeight: 600 }}>
      A new version of Straiv is available.
    </p>
    <p style={{ marginBottom: 20, fontSize: 14, color: '#a0a0a0', maxWidth: 360 }}>
      Tap reload to fetch the latest version.
    </p>
    <button
      type="button"
      onClick={nukeCachesAndReload}
      style={{
        padding: '12px 28px',
        fontSize: 16,
        background: '#00ffa3',
        color: '#000',
        border: 'none',
        borderRadius: 8,
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      Reload
    </button>
  </div>
);

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: unknown): State {
    const err =
      error instanceof Error
        ? error
        : new Error(typeof error === 'string' ? error : 'Unknown error');
    return { hasError: true, error: err };
  }

  public componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    const err = error instanceof Error ? error : new Error(String(error));
    console.error('ErrorBoundary caught an error:', err, errorInfo);

    const errorMsg = err?.message || '';

    // Chunk-load errors: render a single manual-reload screen. Do NOT auto-reload —
    // auto-reloading puts the user into a visible flash/loop on slow networks or
    // when the underlying problem is the cached index.html itself.
    if (isChunkLoadError(err)) {
      return;
    }

    // Custom fallback (e.g. RunningActive load failure) — let parent decide.
    if (this.props.fallback) return;

    // Show user-friendly toast for rate limit errors. Do not auto-reload on
    // generic errors — repeated reloads were creating the production loop.
    if (errorMsg.includes('429') || errorMsg.includes('daily_limit')) {
      toast.error(i18n.t('components.errorBoundary.rateLimitToast'));
    } else {
      toast.error(i18n.t('components.errorBoundary.genericToast'));
    }
  }

  public render() {
    if (this.state.hasError) {
      if (isChunkLoadError(this.state.error)) {
        return <ChunkErrorScreen />;
      }

      return this.props.fallback || this.props.children;
    }

    return this.props.children;
  }
}
